import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Edit, Plus, Trash2, Loader, Download, X } from 'lucide-react';
import useSWR from 'swr';

import NewUserActivityLogService from '../../services/user-activity-log-service';
import NewRoleService from '../../services/role-service';
import { useAuth } from '../../contexts/AuthContext';
import { datetimeFormatter } from '../../utils/DatetimeFormatter';

import { STATUS_MESSAGES } from '../../utils/api-handlers/member-account-form';
import { showToast } from '../../components/ToastConfig';

// Fetch roles data
const rolesFetcher = async () => {
  try {
    const res = await NewRoleService.searchMember();
    return [{ name: '', displayName: 'All' }, ...res.data?.data];
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [{ name: '', displayName: 'All' }];
  }
};

// Fetch events data (你需要根據實際的 API 調整這個函數)
const eventsFetcher = async () => {
  try {
    // 假設你有一個 EventService，需要根據實際情況調整
    // const res = await EventService.getEvents();
    // return [{ id: '', name: 'All' }, ...res.data?.data];
    
    // 暫時使用模擬數據，你可以替換為實際的 API 調用
    return [
      { id: '', name: 'All' },
      { id: 'AccountCreate', name: 'Create Account' },
      { id: 'AccountCustomerCreate', name: 'Create Stripe Customer' },
      { id: 'EditProfile', name: 'Edit Profile' },
      { id: 'ChangePassword', name: 'Change Password' },
      { id: 'Login', name: 'Log In' },
      { id: 'EditCompanyProfile', name: 'Edit Company Profile' },
      { id: 'Subscribed', name: 'Subscribed' },
      { id: 'InvoicePaid', name: 'Invoice Paid' },
      { id: 'CancelSubscription', name: 'Cancel Subscription' },
      { id: 'CreateCompany', name: 'Create Company' },
      { id: 'AddCompany', name: 'Add Company' },
      { id: 'EditCompanyProfile', name: 'Edit Company' },
      { id: 'LeaveCompany', name: 'Leave Company' },
      { id: 'RemovedFromCompany', name: 'Removed From Company' },
      { id: 'InvitedToCompany', name: 'Invited To Company' }
    ];
  } catch (error) {
    console.error('Error fetching events:', error);
    return [{ id: '', name: 'All' }];
  }
};

// SWR fetcher function
const fetcher = async ([url, keyword, role, status, subscriptionLevel, startAt, endAt, event, page]) => {
  try {
    // Transform URL parameters to API parameters
    const apiParams = {
      search: keyword,
      role,
      isEnabled: status === 'inactive' ? false : status === 'active' ? true : null,
      subscriptionLevel: subscriptionLevel || "",
      startAt,
      endAt,
      event: event || ""
    };
    const res = await NewUserActivityLogService.searchLatestOnePerUser(keyword, event, page, startAt, endAt);
    return res.data;
  } catch (error) {
    if (error.response?.status === 400 && ["1002", "8000"].includes(error.response?.data?.errorCode)) {
      return { data: [], pagination: { totalCount: 0 } };
    }
    throw error;
  }
};

function MemberActivityList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('keyword') || '');
  const [pageInput, setPageInput] = useState(searchParams.get('page') || '1');
  const [isComposing, setIsComposing] = useState(false);
  const Navigate = useNavigate();
  const { logout } = useAuth();

  // Get search parameters from URL
  const keyword = searchParams.get('keyword') || '';
  const role = searchParams.get('role') || '';
  const status = searchParams.get('status') || '';
  const subscriptionLevel = searchParams.get('subscriptionLevel') || '';
  const startAt = searchParams.get('startAt') || '';
  const endAt = searchParams.get('endAt') || '';
  const event = searchParams.get('event') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = 10;

  // Fetch roles data
  const { data: rolesData } = useSWR('/api/Role/SearchMember', rolesFetcher, {
    revalidateOnFocus: false
  });
  const roleOptions = rolesData || [{ name: '', displayName: 'All' }];

  // Fetch events data
  const { data: eventsData } = useSWR('/api/Events', eventsFetcher, {
    revalidateOnFocus: false
  });
  const eventOptions = eventsData || [{ id: '', name: 'All' }];

  // Use SWR to fetch data
  const { data, error, isLoading, mutate } = useSWR(
    ['/api/MemberAccountManagement/Search', keyword, role, status, subscriptionLevel, startAt, endAt, event, page],
    fetcher,
    {
      onError: (err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          logout();
        } else if (err.response?.status !== 400) {
          const errorMessage = STATUS_MESSAGES[err.response?.status] || STATUS_MESSAGES["default"];
          showToast.error(errorMessage);
        } else {
          const errorMessage = STATUS_MESSAGES[err.response?.data?.errorCode] || STATUS_MESSAGES["default"];
          showToast.error(errorMessage);
        }
      },
      revalidateOnFocus: false,
    }
  );

  const [accountsWithStatus, setAccountsWithStatus] = useState([]);
  const records = data?.data || [];
  const totalItems = data?.pagination?.totalCount || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (records.length > 0) {
      setAccountsWithStatus(records.map(record => ({
        ...record,
        isUpdating: false
      })));
    }
  }, [records]);

  // Handle search params update
  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
    if (updates.page) {
      setPageInput(updates.page);
    }
  }, [searchParams, setSearchParams]);

  // Search debounce
  useEffect(() => {
    if (isComposing) return;
    if (searchInput === keyword) return;

    const timer = setTimeout(() => {
      updateSearchParams({ keyword: searchInput, page: '1' });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, isComposing, updateSearchParams, keyword]);

  // Export handler
  const handleExport = async () => {
    try {
      // Prepare export parameters with current filters
      const exportParams = {
        // search: keyword,
        // role,
        // isEnabled: status === 'inactive' ? false : status === 'active' ? true : null,
        // subscriptionLevel: subscriptionLevel || "",
        startAt,
        endAt,
        activityType: event || "",
      };

      const res = await NewUserActivityLogService.exportToExcel(exportParams);
      window.open(`${process.env.REACT_APP_FILE_BASIC}${res.data}`, "_blank");      
      showToast.success('Member activity report exported successfully.');
    } catch (error) {
      console.error('Error exporting data:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else {
        showToast.error('Export failed, please try again.');
      }
    }
  };

  return (
    <div className={`p-6 pt-0 ${totalPages > 1 ? "h-[78vh]" : ""} overflow-auto`}>
      <div className="container mx-auto">
        <div className="sticky top-0 pt-6 pb-4 bg-white z-10">
          <div className='flex justify-between items-center'>
            <h1 className="text-2xl font-bold">Members Activity</h1>
            <div className='flex items-center space-x-4'>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2"
              >
                <Download size={20} />
                <span>Export</span>
              </button>
              <div className='relative'>
                <input
                  type="date"
                  value={startAt}
                  onChange={(e) => updateSearchParams({ startAt: e.target.value, page: '1' })}
                  className="px-3 py-2 border rounded"
                  placeholder="Start Date"
                />
                <span className='bg-white p-1 block absolute top-0 left-3 -translate-y-1/2 text-sm text-gray-600'>From Date</span>
              </div>
              <div className='relative'>
                <input
                  type="date"
                  value={endAt}
                  onChange={(e) => updateSearchParams({ endAt: e.target.value, page: '1' })}
                  className="px-3 py-2 border rounded"
                  placeholder="End Date"
                />
                <span className='bg-white p-1 block absolute top-0 left-3 -translate-y-1/2 text-sm text-gray-600'>To Date</span>
              </div>
            </div>
          </div>
          
          {/* Search and filter controls */}
          <div className="space-y-4">
            <div className="flex justify-end mt-4">
              {/* <div className="flex items-center gap-4">
                <Link className='flex items-center gap-2' to="/member/account/new">
                    <Plus size={28} className='border border-gray-600 rounded-full p-1' />
                    <span>Create</span>
                </Link>
              </div> */}
              <div className="flex gap-4">
                <input
                  type="text"
                  placeholder="Search Account..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  className="px-3 py-2 border rounded"
                />
                {/* <select
                  value={role || 'placeholder-role'}
                  onChange={(e) => {
                    const value = e.target.value === 'placeholder-role' ? '' : e.target.value;
                    updateSearchParams({ role: value, page: '1' });
                  }}
                  className="px-3 py-2 border rounded"
                >
                  <option value="placeholder-role" disabled hidden>User Role</option>
                  {roleOptions.map((option, index) => (
                    <option key={option.name || index} value={option.name}>{option.displayName}</option>
                  ))}
                </select>
                <select
                  value={status || 'placeholder-status'}
                  onChange={(e) => {
                    const value = e.target.value === 'placeholder-status' ? '' : e.target.value;
                    updateSearchParams({ status: value, page: '1' });
                  }}
                  className="px-3 py-2 border rounded"
                >
                  <option value="placeholder-status" disabled hidden>Status</option>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={subscriptionLevel || 'placeholder-plan'}
                  onChange={(e) => {
                    const value = e.target.value === 'placeholder-plan' ? '' : e.target.value;
                    updateSearchParams({ subscriptionLevel: value, page: '1' });
                  }}
                  className="px-3 py-2 border rounded"
                >
                  <option value="placeholder-plan" disabled hidden>Current Plan</option>
                  {SUBSCRIPTION_LEVEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select> */}
                <div className='relative'>
                  <select
                    value={event || 'placeholder-event'}
                    onChange={(e) => {
                      const value = e.target.value === 'placeholder-event' ? '' : e.target.value;
                      updateSearchParams({ event: value, page: '1' });
                    }}
                    className="px-3 py-[0.66rem] border rounded"
                  >
                    <option value="placeholder-event" disabled hidden>Choose Event</option>
                    {eventOptions.map((option, index) => (
                      <option key={option.id || index} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                  <span className='bg-white p-1 block absolute top-0 left-3 -translate-y-1/2 text-sm text-gray-600'>Event</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="text-center py-8 text-gray-500 flex items-center justify-center">
            <Loader className="mr-1 animate-spin w-5 h-5" />
            Loading...
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="text-center py-8 text-red-500">
            Error when loading data. Please try again or refresh the page.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && records.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {keyword || role || status || subscriptionLevel || startAt || endAt || event ? 'No activities matching the criteria' : 'No activities existing currently'}
          </div>
        )}

        {/* Account list */}
        {!isLoading && !error && records.length > 0 && (
          <div className="space-y-2">
            {/* Table header */}
            <div className="flex items-center p-4 bg-gray-50 rounded-t border-b font-medium justify-between">
              <div className="flex-1 flex">
                <div className="w-[20%]">Member Name</div>
                <div className="w-[30%]">Email</div>
                <div className="w-[25%]">Active Event</div>
                <div className="w-[25%]">Time</div>
              </div>
            </div>

            {/* Table body */}
            {records.map(record => (
              <Link key={record.id} to={`/member/activity/${record?.user?.id}`} className="flex items-center p-4 border rounded hover:bg-gray-50 justify-between">
                <div className="flex-1 flex">
                  <div className="w-[20%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{record?.user?.firstName} {record?.user?.lastName}</div>
                  <div className="w-[30%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{record?.user?.email}</div>
                  <div className="w-[25%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{record?.activityTypeName}</div>
                  <div className="w-[25%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{record?.createdAt ? datetimeFormatter(record.createdAt) : "(No data)"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-4 fixed bottom-[4%] w-[calc(100%-21rem)] max-w-[1200px]">
            <div className="flex items-center">
              <span className="text-gray-700">Page</span>
              <input
                type="text"
                className="w-16 mx-2 px-2 py-1 border rounded text-center"
                value={pageInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setPageInput(value);
                }}
                onBlur={() => {
                  const pageNum = parseInt(pageInput);
                  if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
                    setPageInput(page.toString());
                  }
                }}
              />
            </div>
            
            {/* Pagination buttons */}
            <div className="flex flex-wrap justify-center gap-2 w-full md:w-auto px-4">
              <button
                onClick={() => updateSearchParams({ page: String(page - 1) })}
                disabled={page === 1}
                className={`px-3 py-1 border rounded ${
                  page === 1 ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                Prev
              </button>
              
              {/* First page */}
              <button
                onClick={() => updateSearchParams({ page: '1' })}
                className={`px-3 py-1 border rounded ${
                  1 === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                }`}
              >
                1
              </button>

              {/* Pagination dots and numbers */}
              {page > 6 && <span className="px-3 py-1">...</span>}

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(pageNum => {
                  const showRange = 4;
                  return pageNum > page - showRange && pageNum < page + showRange;
                })
                .filter(pageNum => pageNum !== 1 && pageNum !== totalPages)
                .map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    className={`px-3 py-1 border rounded ${
                      pageNum === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

              {page < totalPages - 5 && <span className="px-3 py-1">...</span>}

              {/* Last page */}
              {totalPages > 1 && (
                <button
                  onClick={() => updateSearchParams({ page: String(totalPages) })}
                  className={`px-3 py-1 border rounded ${
                    totalPages === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() => updateSearchParams({ page: String(page + 1) })}
                disabled={page === totalPages}
                className={`px-3 py-1 border rounded ${
                  page === totalPages ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MemberActivityList;